import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
	Plus,
	Edit,
	Eye,
	Search,
	Filter,
	Upload,
	X,
	CheckCircle,
	Clock,
	AlertCircle,
	ShoppingBag,
	User,
	Calendar,
	Tag,
	Image as ImageIcon,
	Paperclip,
	ChevronLeft,
	ChevronRight,
	RefreshCw,
	Loader,
	Wand2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { listingsService, dressStatusService, branchListingsService, showToast } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import ListingWizard from '../../components/ListingWizard';

const Listings = () => {
	const { user } = useAuth();
	const [listings, setListings] = useState([]);
	const [showModal, setShowModal] = useState(false);
	const [viewModal, setViewModal] = useState(false);
	const [editingListing, setEditingListing] = useState(null);
	const [viewingListing, setViewingListing] = useState(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [statusFilter, setStatusFilter] = useState('all');
	const [selectedImages, setSelectedImages] = useState([]);
	
	// Wizard state
	const [showWizard, setShowWizard] = useState(false);
	
	// Check if user is branch manager
	const isBranchManager = user?.role === 'branch manager';

	// API-related state
	const [loading, setLoading] = useState(false);
	const [pagination, setPagination] = useState({
		current_page: 1,
		last_page: 1,
		per_page: 15,
		total: 0,
		from: 0,
		to: 0
	});
	const [dressStatuses, setDressStatuses] = useState([]);
	const [statistics, setStatistics] = useState(null);

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors }
	} = useForm();



	// Fetch listings from API
	const fetchListings = async (params = {}) => {
		setLoading(true);
		try {
			// Use appropriate service based on user role
			const service = isBranchManager ? branchListingsService : listingsService;
			
			const requestParams = {
				page: pagination.current_page,
				per_page: pagination.per_page,
				search: searchTerm,
				status: statusFilter === 'all' ? undefined : statusFilter,
				...params
			};
			
			// For branch managers, we'll filter on frontend to show items from "in transit" onwards
			// Don't add any status filter to the API call - we'll filter the results
			
			const response = await service.getAll(requestParams);

			if (response.success) {
				let filteredData = response.data;
				
				// For branch managers, filter to show only items from "in transit" onwards (status 3-9)
				if (isBranchManager) {
					filteredData = response.data.filter(listing => {
						const dressStatusId = listing.dress_status_id || listing.status;
						return dressStatusId >= 3; // Show status 3 (In Transit) and above
					});
				}
				
				setListings(filteredData);
				setPagination(response.meta);
			} else {
				showToast.error('Failed to fetch listings');
			}
		} catch (error) {
			console.error('Error fetching listings:', error);
			showToast.error(error.message || 'Failed to fetch listings');
		} finally {
			setLoading(false);
		}
	};

	// Fetch dress statuses
	const fetchDressStatuses = async () => {
		try {
			const response = await dressStatusService.getAll();
			if (response.success) {
				setDressStatuses(response.data);
			} else {
				showToast.error('Failed to fetch dress statuses');
			}
		} catch (error) {
			console.error('Error fetching dress statuses:', error);
			showToast.error('Error fetching dress statuses');
		}
	};

	// Fetch dress statuses and statistics
	const fetchStatistics = async () => {
		try {
			const response = await listingsService.getStatistics();
			if (response.success) {
				setStatistics(response.data);
				setDressStatuses(response.data.dress_status_breakdown || []);
			}
		} catch (error) {
			console.error('Error fetching statistics:', error);
		}
	};

	// Load data on component mount
	useEffect(() => {
		fetchListings();
		fetchDressStatuses();
		// fetchStatistics();
	}, []);

	// Refetch when filters change
	// useEffect(() => {
	// 	const timeoutId = setTimeout(() => {
	// 		fetchListings({ page: 1 });
	// 	}, 500); // Debounce search

	// 	return () => clearTimeout(timeoutId);
	// }, [searchTerm, statusFilter]);

	const getStatusColor = (status) => {
		const statusMap = {
			// String-based statuses (new system)
			'pending': '#F59E0B',
			'listed': '#3B82F6',
			'in-process': '#3B82F6',
			'in-transit': '#8B5CF6',
			'at-store': '#10B981',
			'authenticated': '#059669',
			'rejected': '#DC2626',
			'disposed': '#6B7280',
			'returned-to-seller': '#7C3AED',
			'de-listed': '#9CA3AF',
			// Numeric-based statuses (current system)
			0: '#F59E0B',   // pending (legacy)
			1: '#F59E0B',   // pending
			2: '#3B82F6',   // listed
			3: '#8B5CF6',   // in-transit
			4: '#10B981',   // at-store
			5: '#059669',   // authenticated
			6: '#DC2626',   // rejected
			7: '#6B7280',   // disposed
			8: '#7C3AED',   // returned-to-seller
			9: '#9CA3AF'    // de-listed
		};
		return statusMap[status] || '#6B7280';
	};

	const getStatusIcon = (status) => {
		// Handle both string and numeric status values
		const iconMap = {
			// String-based statuses (new system)
			'pending': Clock,
			'listed': CheckCircle,
			'in-process': Clock,
			'in-transit': Clock,
			'at-store': ShoppingBag,
			'authenticated': CheckCircle,
			'rejected': AlertCircle,
			'disposed': AlertCircle,
			'returned-to-seller': CheckCircle,
			'de-listed': AlertCircle,
			// Numeric-based statuses (current system)
			0: Clock,        // pending (legacy)
			1: Clock,        // pending
			2: CheckCircle,  // listed
			3: Clock,        // in-transit
			4: ShoppingBag,  // at-store
			5: CheckCircle,  // authenticated
			6: AlertCircle,  // rejected
			7: AlertCircle,  // disposed
			8: CheckCircle,  // returned-to-seller
			9: AlertCircle   // de-listed
		};
		return iconMap[status] || AlertCircle;
	};

	// Pagination handlers
	const handlePageChange = (page) => {
		fetchListings({ page });
	};

	const handlePerPageChange = (perPage) => {
		setPagination(prev => ({ ...prev, per_page: perPage }));
		fetchListings({ page: 1, per_page: perPage });
	};

	// Wizard handlers
	const handleWizardComplete = async (wizardData) => {
		try {
			setLoading(true);
			const payload = {
				dress_status_id: wizardData.selectedStatus.id,
				admin_notes: wizardData.notes
			};
			// Include branch assignment if provided (required for Listed)
			if (wizardData.branchId) {
				payload.branch_id = wizardData.branchId;
			}
			const response = await listingsService.updateStatus(wizardData.listingId, payload);
			
			if (response.success) {
				showToast.success(`Listing ${wizardData.listingId} status updated to ${wizardData.selectedStatus.name}`);
				// Refresh listings to show updated status
				await fetchListings();
			} else {
				showToast.error('Failed to update listing status');
			}
		} catch (error) {
			console.error('Error updating listing status:', error);
			showToast.error('Error updating listing status');
		} finally {
			setLoading(false);
		}
	};

	const handleWizardClose = () => {
		setShowWizard(false);
	};

	// Handle status filter click
	const handleStatusFilter = (statusSlug) => {
		setStatusFilter(statusSlug);
		fetchListings({ 
			page: 1, 
			status: statusSlug === 'all' ? undefined : statusSlug 
		});
	};

	const openModal = (listing = null) => {
		setEditingListing(listing);
		setShowModal(true);
		setSelectedImages([]);

		if (listing) {
			reset({
				title: listing.title,
				description: listing.description,
				designer: listing.designer,
				original_price: listing.original_price,
				selling_price: listing.selling_price,
				size: listing.size,
				color: listing.color,
				category: listing.category,
				condition: listing.condition,
				status: listing.status,
				listing_content: listing.listing_content || ''
			});
			setSelectedImages(listing.images || []);
		} else {
			reset({
				title: '',
				description: '',
				designer: '',
				original_price: '',
				selling_price: '',
				size: '',
				color: '',
				category: '',
				condition: 'Excellent',
				status: 'pending',
				listing_content: ''
			});
		}
	};

	const openViewModal = async (listing) => {
		try {
			setLoading(true);
			console.log('🔍 Fetching listing details for:', listing.listing_id);
			
			const response = await listingsService.getById(listing.listing_id);
			
			if (response.success) {
				console.log('✅ Listing details fetched successfully:', response.data);
				setViewingListing(response.data);
				setViewModal(true);
			} else {
				console.error('❌ Failed to fetch listing details:', response);
				showToast.error('Failed to fetch listing details');
			}
		} catch (error) {
			console.error('❌ Error fetching listing details:', error);
			showToast.error('Error fetching listing details');
		} finally {
			setLoading(false);
		}
	};

	const closeModal = () => {
		setShowModal(false);
		setViewModal(false);
		setEditingListing(null);
		setViewingListing(null);
		setSelectedImages([]);
		reset();
	};

	const handleImageUpload = (e) => {
		const files = Array.from(e.target.files);
		if (selectedImages.length + files.length > 25) {
			toast.error('Maximum 25 images allowed');
			return;
		}

		files.forEach(file => {
			const reader = new FileReader();
			reader.onloadend = () => {
				setSelectedImages(prev => [...prev, reader.result]);
			};
			reader.readAsDataURL(file);
		});
	};

	const removeImage = (index) => {
		setSelectedImages(prev => prev.filter((_, i) => i !== index));
	};


	const onSubmit = (data) => {
		const listingData = {
			...data,
			images: selectedImages,
			// Keep existing proof_images and defect_images when editing
			proof_images: editingListing ? editingListing.proof_images : [],
			defect_images: editingListing ? editingListing.defect_images : [],
			seller_name: 'Current User', // Would come from auth context
			seller_id: 1
		};

		if (editingListing) {
			const updatedListings = listings.map(listing =>
				listing.id === editingListing.id
					? { ...listing, ...listingData, updated_at: new Date().toISOString().split('T')[0] }
					: listing
			);
			setListings(updatedListings);
			toast.success('Listing updated successfully!');
		} else {
			const newListing = {
				id: Math.max(...listings.map(l => l.id)) + 1,
				...listingData,
				created_at: new Date().toISOString().split('T')[0],
				updated_at: new Date().toISOString().split('T')[0]
			};
			setListings([...listings, newListing]);
			toast.success('Listing created successfully!');
		}
		closeModal();
	};

	const updateStatus = (id, newStatus, authenticatorName = null) => {
		const updatedListings = listings.map(listing =>
			listing.id === id
				? {
					...listing,
					status: newStatus,
					authenticator_name: authenticatorName,
					updated_at: new Date().toISOString().split('T')[0]
				}
				: listing
		);
		setListings(updatedListings);
		toast.success(`Listing status updated to ${newStatus}!`);
	};


	return (
		<div>
			<div className="card">
				<div style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '24px'
				}}>
					<div>
						<h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111827' }}>
							{isBranchManager ? 'Items in Transit & Beyond' : 'Listings Management'}
						</h1>
						{isBranchManager && (
							<p style={{ color: '#6b7280', margin: '4px 0 0', fontSize: '14px' }}>
								Items from "In Transit" status onwards - tracking shipments and store inventory
							</p>
						)}
					</div>
					<div style={{ display: 'flex', gap: '12px' }}>
						<button
							onClick={() => fetchListings()}
							className="btn btn-secondary"
							disabled={loading}
							title="Refresh Listings"
						>
							<RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
							Refresh
						</button>
					</div>
				</div>

				{/* Dress Status Wizard - Hidden for branch managers */}
				{!isBranchManager && (
				<div style={{
					marginBottom: '24px',
					padding: '20px',
					background: '#f8fafc',
					borderRadius: '8px',
					border: '1px solid #e2e8f0'
				}}>
					<h3 style={{
						fontSize: '16px',
						fontWeight: '600',
						color: '#374151',
						marginBottom: '16px',
						display: 'flex',
						alignItems: 'center',
						gap: '8px'
					}}>
						<Wand2 size={18} />
						Quick Status Filter
					</h3>
					<div style={{
						display: 'flex',
						gap: '12px',
						flexWrap: 'wrap'
					}}>
						{/* All Status Button */}
						<button
							onClick={() => handleStatusFilter('all')}
							className={`btn ${statusFilter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '8px',
								padding: '8px 16px',
								borderRadius: '20px',
								fontSize: '14px',
								fontWeight: '500',
								border: statusFilter === 'all' ? '2px solid #3b82f6' : '2px solid transparent'
							}}
						>
							<div style={{
								width: '8px',
								height: '8px',
								borderRadius: '50%',
								backgroundColor: '#6b7280'
							}}></div>
							All Status
						</button>

						{/* Dress Status Buttons */}
						{dressStatuses.map((status) => (
							<button
								key={status.id}
								onClick={() => handleStatusFilter(status.slug)}
								className={`btn ${statusFilter === status.slug ? 'btn-primary' : 'btn-secondary'}`}
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '8px',
									padding: '8px 16px',
									borderRadius: '20px',
									fontSize: '14px',
									fontWeight: '500',
									border: statusFilter === status.slug ? '2px solid #3b82f6' : '2px solid transparent',
									backgroundColor: statusFilter === status.slug ? '#3b82f6' : '#ffffff',
									color: statusFilter === status.slug ? '#ffffff' : '#374151'
								}}
							>
								<div style={{
									width: '8px',
									height: '8px',
									borderRadius: '50%',
									backgroundColor: status.color || '#6b7280'
								}}></div>
								{status.name}
							</button>
						))}
					</div>
				</div>
				)}

				{/* Filters */}
				<div style={{
					display: 'flex',
					gap: '16px',
					marginBottom: '20px',
					flexWrap: 'wrap'
				}}>
					{/* Search */}
					<div style={{ position: 'relative', width: '250px' }}>
						<Search
							size={18}
							style={{
								position: 'absolute',
								left: '12px',
								top: '50%',
								transform: 'translateY(-50%)',
								color: '#6b7280'
							}}
						/>
						<input
							type="text"
							placeholder={isBranchManager ? "Search items in transit & beyond..." : "Search listings..."}
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							className="search-box"
							style={{ paddingLeft: '40px', width: '100%', height: '44px' }}
						/>
					</div>

					{/* Status Filter */}
					<div style={{ position: 'relative', width: '250px' }}>
						<Filter
							size={18}
							style={{
								position: 'absolute',
								left: '12px',
								top: '50%',
								transform: 'translateY(-50%)',
								color: '#6b7280'
							}}
						/>
						<select
							value={statusFilter}
							onChange={(e) => setStatusFilter(e.target.value)}
							className="form-select"
							style={{ paddingLeft: '40px', width: '100%', height: '44px' }}
						>
							<option value="all">All Status</option>
							{dressStatuses.map((status) => (
								<option key={status.slug} value={status.slug}>
									{status.name} ({status.count})
								</option>
							))}
						</select>
					</div>
				</div>

				{/* Loading State */}
				{loading && (
					<div style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						padding: '40px',
						gap: '12px'
					}}>
						<Loader className="animate-spin" size={24} color="#6b7280" />
						<span style={{ color: '#6b7280' }}>Loading listings...</span>
					</div>
				)}

				{/* Listings Table */}
				{!loading && (
					<div style={{ overflowX: 'auto' }}>
						<table className="table">
							<thead>
								<tr>
									<th style={{ width: '80px' }}>Image</th>
									<th>Listing ID</th>
									<th>Username</th>
									<th>Status</th>
									<th>Defects</th>
									<th>Created Date</th>
									<th style={{ width: '100px' }}>Actions</th>
								</tr>
							</thead>
							<tbody>
								{listings.map((listing) => {
									const StatusIcon = getStatusIcon(listing.status || 'pending');
									return (
										<tr key={listing.listing_id}>
											{/* Image */}
											<td>
												<div style={{
													width: '60px',
													height: '60px',
													backgroundColor: '#f3f4f6',
													borderRadius: '8px',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													overflow: 'hidden',
													position: 'relative'
												}}>
													{listing.front_image ? (
														<img
															src={listing.front_image}
															alt="Listing"
															style={{
																width: '100%',
																height: '100%',
																objectFit: 'cover'
															}}
														/>
													) : (
														<ImageIcon size={24} color="#9ca3af" />
													)}
												</div>
											</td>

											{/* Listing ID */}
											<td>
												<div style={{
													fontWeight: '600',
													color: '#111827',
													fontSize: '14px'
												}}>
													{listing.listing_id}
												</div>
											</td>

											{/* Username */}
											<td>
												<div style={{
													display: 'flex',
													alignItems: 'center',
													gap: '6px'
												}}>
													<User size={14} color="#6b7280" />
													<span style={{ fontSize: '14px' }}>{listing.username}</span>
												</div>
											</td>

											{/* Status */}
											<td>
												<div style={{
													display: 'flex',
													alignItems: 'center',
													gap: '4px',
													backgroundColor: getStatusColor(listing.status || 'pending'),
													color: 'white',
													padding: '4px 8px',
													borderRadius: '12px',
													fontSize: '12px',
													fontWeight: '500',
													width: 'fit-content'
												}}>
													<StatusIcon size={12} />
													{listing.status_text || (typeof listing.status === 'string' ? listing.status.charAt(0).toUpperCase() + listing.status.slice(1).replace('-', ' ') : 'Pending')}
												</div>
											</td>

											{/* Defects */}
											<td>
												<div style={{
													display: 'flex',
													flexWrap: 'wrap',
													gap: '4px',
													maxWidth: '200px'
												}}>
													{listing.defects && listing.defects.length > 0 ? (
														listing.defects.slice(0, 3).map((defect, index) => (
															<span
																key={index}
																style={{
																	backgroundColor: '#FEF3C7',
																	color: '#92400E',
																	padding: '2px 6px',
																	borderRadius: '8px',
																	fontSize: '10px',
																	fontWeight: '500',
																	border: '1px solid #FCD34D',
																	whiteSpace: 'nowrap',
																	overflow: 'hidden',
																	textOverflow: 'ellipsis',
																	maxWidth: '80px'
																}}
																title={defect}
															>
																{defect}
															</span>
														))
													) : (
														<span style={{
															color: '#6b7280',
															fontSize: '12px',
															fontStyle: 'italic'
														}}>
															No defects
														</span>
													)}
													{listing.defects && listing.defects.length > 3 && (
														<span style={{
															backgroundColor: '#E5E7EB',
															color: '#6B7280',
															padding: '2px 6px',
															borderRadius: '8px',
															fontSize: '10px',
															fontWeight: '500'
														}}>
															+{listing.defects.length - 3}
														</span>
													)}
												</div>
											</td>

											{/* Created Date */}
											<td>
												<div style={{
													display: 'flex',
													alignItems: 'center',
													gap: '4px'
												}}>
													<Calendar size={12} color="#6b7280" />
													<span style={{ fontSize: '12px', color: '#6b7280' }}>
														{listing.created_date}
													</span>
												</div>
											</td>

											{/* Actions */}
											<td>
												<div style={{
													display: 'flex',
													gap: '6px'
												}}>
													<button
														onClick={() => window.location.href = `/listing-details/${listing.listing_id}`}
														className="btn btn-secondary"
														style={{
															fontSize: '11px',
															padding: '8px',
															minWidth: 'auto'
														}}
														title="View Details"
													>
														<Eye size={14} />
													</button>
												</div>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}

				{/* No Data State */}
				{!loading && listings.length === 0 && (
					<div style={{
						textAlign: 'center',
						padding: '40px',
						color: '#6b7280'
					}}>
						No listings found.
					</div>
				)}

				{/* Pagination */}
				{!loading && listings.length > 0 && (
					<div style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						marginTop: '20px',
						padding: '16px 0',
						borderTop: '1px solid #e5e7eb'
					}}>
						<div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
							<span style={{ fontSize: '14px', color: '#6b7280' }}>
								Showing {pagination.from} to {pagination.to} of {pagination.total} results
							</span>
							<select
								value={pagination.per_page}
								onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
								className="form-select"
								style={{ width: '80px', height: '36px' }}
							>
								<option value={10}>10</option>
								<option value={15}>15</option>
								<option value={25}>25</option>
								<option value={50}>50</option>
							</select>
						</div>

						<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							<button
								onClick={() => handlePageChange(pagination.current_page - 1)}
								disabled={pagination.current_page === 1}
								className="btn btn-secondary"
								style={{
									padding: '8px 12px',
									fontSize: '12px',
									opacity: pagination.current_page === 1 ? 0.5 : 1
								}}
							>
								Previous
							</button>

							<div style={{ display: 'flex', gap: '4px' }}>
								{Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
									const page = i + 1;
									return (
										<button
											key={page}
											onClick={() => handlePageChange(page)}
											className={`btn ${page === pagination.current_page ? 'btn-primary' : 'btn-secondary'}`}
											style={{
												padding: '8px 12px',
												fontSize: '12px',
												minWidth: '36px'
											}}
										>
											{page}
										</button>
									);
								})}
							</div>

							<button
								onClick={() => handlePageChange(pagination.current_page + 1)}
								disabled={pagination.current_page === pagination.last_page}
								className="btn btn-secondary"
								style={{
									padding: '8px 12px',
									fontSize: '12px',
									opacity: pagination.current_page === pagination.last_page ? 0.5 : 1
								}}
							>
								Next
							</button>
						</div>
					</div>
				)}
			</div>

			{/* Add/Edit Modal */}
			{showModal && (
				<div className="modal-overlay" onClick={closeModal}>
					<div
						className="modal"
						onClick={(e) => e.stopPropagation()}
						style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}
					>
						<div className="modal-header">
							<h2 className="modal-title">
								{editingListing ? 'Edit Listing' : 'Add New Listing'}
							</h2>
							<button onClick={closeModal} className="close-btn">
								×
							</button>
						</div>

						<form onSubmit={handleSubmit(onSubmit)}>
							{/* Basic Information */}
							<div style={{ marginBottom: '24px' }}>
								<h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#111827' }}>
									Basic Information
								</h3>

								<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
									<div className="form-group">
										<label className="form-label">Title</label>
										<input
											type="text"
											className="form-input"
											placeholder="Enter listing title"
											{...register('title', { required: 'Title is required' })}
										/>
										{errors.title && (
											<div className="error-message">{errors.title.message}</div>
										)}
									</div>

									<div className="form-group">
										<label className="form-label">Designer/Brand</label>
										<input
											type="text"
											className="form-input"
											placeholder="Enter designer name"
											{...register('designer', { required: 'Designer is required' })}
										/>
										{errors.designer && (
											<div className="error-message">{errors.designer.message}</div>
										)}
									</div>
								</div>

								<div className="form-group">
									<label className="form-label">Description</label>
									<textarea
										className="form-input"
										rows={3}
										placeholder="Describe the item condition, usage, etc."
										{...register('description', { required: 'Description is required' })}
									/>
									{errors.description && (
										<div className="error-message">{errors.description.message}</div>
									)}
								</div>
							</div>

							{/* Listing Content */}
							<div style={{ marginBottom: '24px' }}>
								<h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#111827' }}>
									Listing Description
								</h3>

								<div className="form-group">
									<label className="form-label">Description</label>
									<textarea
										className="form-input"
										rows={10}
										placeholder="Write detailed content about the listing..."
										{...register('listing_content')}
										style={{
											fontFamily: 'inherit',
											lineHeight: '1.6'
										}}
									/>
									<div style={{
										fontSize: '12px',
										color: '#6b7280',
										marginTop: '4px'
									}}>
										You can add detailed descriptions, product history, care instructions, etc.
									</div>
								</div>
							</div>

							{/* Pricing & Details */}
							<div style={{ marginBottom: '24px' }}>
								<h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#111827' }}>
									Pricing & Details
								</h3>

								<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
									<div className="form-group">
										<label className="form-label">Original Price ($)</label>
										<input
											type="number"
											className="form-input"
											placeholder="0.00"
											{...register('original_price', { required: 'Original price is required' })}
										/>
										{errors.original_price && (
											<div className="error-message">{errors.original_price.message}</div>
										)}
									</div>

									<div className="form-group">
										<label className="form-label">Selling Price ($)</label>
										<input
											type="number"
											className="form-input"
											placeholder="0.00"
											{...register('selling_price', { required: 'Selling price is required' })}
										/>
										{errors.selling_price && (
											<div className="error-message">{errors.selling_price.message}</div>
										)}
									</div>

									<div className="form-group">
										<label className="form-label">Size</label>
										<select
											className="form-select"
											{...register('size', { required: 'Size is required' })}
										>
											<option value="">Select size</option>
											<option value="XS">XS</option>
											<option value="S">S</option>
											<option value="M">M</option>
											<option value="L">L</option>
											<option value="XL">XL</option>
											<option value="XXL">XXL</option>
											<option value="One Size">One Size</option>
										</select>
										{errors.size && (
											<div className="error-message">{errors.size.message}</div>
										)}
									</div>
								</div>

								<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
									<div className="form-group">
										<label className="form-label">Color</label>
										<input
											type="text"
											className="form-input"
											placeholder="Enter color"
											{...register('color', { required: 'Color is required' })}
										/>
										{errors.color && (
											<div className="error-message">{errors.color.message}</div>
										)}
									</div>

									<div className="form-group">
										<label className="form-label">Category</label>
										<select
											className="form-select"
											{...register('category', { required: 'Category is required' })}
										>
											<option value="">Select category</option>
											<option value="Dresses">Dresses</option>
											<option value="Tops">Tops</option>
											<option value="Bottoms">Bottoms</option>
											<option value="Jackets">Jackets</option>
											<option value="Accessories">Accessories</option>
											<option value="Shoes">Shoes</option>
										</select>
										{errors.category && (
											<div className="error-message">{errors.category.message}</div>
										)}
									</div>

									<div className="form-group">
										<label className="form-label">Condition</label>
										<select
											className="form-select"
											{...register('condition', { required: 'Condition is required' })}
										>
											<option value="Excellent">Excellent</option>
											<option value="Very Good">Very Good</option>
											<option value="Good">Good</option>
											<option value="Fair">Fair</option>
										</select>
										{errors.condition && (
											<div className="error-message">{errors.condition.message}</div>
										)}
									</div>
								</div>

							</div>

							{/* Images - Display Only */}
							{editingListing && editingListing.images && editingListing.images.length > 0 && (
								<div style={{ marginBottom: '24px' }}>
									<h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#111827' }}>
										Product Images
									</h3>

									<div style={{
										padding: '12px',
										backgroundColor: '#f9fafb',
										borderRadius: '8px',
										border: '1px solid #e5e7eb'
									}}>
										<div style={{
											display: 'flex',
											gap: '8px',
											overflowX: 'auto',
											paddingBottom: '8px'
										}}>
											{editingListing.images.slice(0, 20).map((image, index) => (
												<img
													key={index}
													src={image}
													alt={`Product ${index + 1}`}
													style={{
														width: '100px',
														height: '100px',
														objectFit: 'cover',
														borderRadius: '4px',
														border: '1px solid #e5e7eb',
														cursor: 'pointer',
														flexShrink: 0
													}}
													onClick={() => window.open(image, '_blank')}
													title="Click to view full size"
												/>
											))}
										</div>
									</div>

									<div style={{
										fontSize: '12px',
										color: '#6b7280',
										marginTop: '8px',
										textAlign: 'center'
									}}>
										Showing {Math.min(editingListing.images.length, 20)} of {editingListing.images.length} image{editingListing.images.length !== 1 ? 's' : ''} • Scroll to view more • Click to view full size
									</div>
								</div>
							)}

							{/* Additional Images */}
							<div style={{ marginBottom: '24px' }}>
								<h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#111827' }}>
									Additional Images
								</h3>

								<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
									{/* Proof of Purchase Images - Display Only */}
									<div>
										<label className="form-label">Proof of Purchase Images</label>
										{editingListing && editingListing.proof_images && editingListing.proof_images.length > 0 ? (
											<div style={{
												padding: '12px',
												backgroundColor: '#f9fafb',
												borderRadius: '8px',
												border: '1px solid #e5e7eb'
											}}>
												<div style={{
													display: 'grid',
													gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
													gap: '8px'
												}}>
													{editingListing.proof_images.map((image, index) => (
														<img
															key={index}
															src={image}
															alt={`Proof of purchase ${index + 1}`}
															style={{
																width: '100%',
																height: '80px',
																objectFit: 'cover',
																borderRadius: '4px',
																border: '1px solid #e5e7eb',
																cursor: 'pointer'
															}}
															onClick={() => window.open(image, '_blank')}
															title="Click to view full size"
														/>
													))}
												</div>
												<div style={{
													fontSize: '12px',
													color: '#6b7280',
													marginTop: '8px',
													textAlign: 'center'
												}}>
													{editingListing.proof_images.length} image{editingListing.proof_images.length !== 1 ? 's' : ''} • Click to view full size
												</div>
											</div>
										) : (
											<div style={{
												padding: '16px',
												backgroundColor: '#f9fafb',
												borderRadius: '8px',
												border: '1px solid #e5e7eb',
												textAlign: 'center',
												color: '#6b7280'
											}}>
												No proof of purchase images available
											</div>
										)}
									</div>

									{/* Defects/Wear Images - Display Only */}
									<div>
										<label className="form-label">Defects/Wear Images</label>
										{editingListing && editingListing.defect_images && editingListing.defect_images.length > 0 ? (
											<div style={{
												padding: '12px',
												backgroundColor: '#f9fafb',
												borderRadius: '8px',
												border: '1px solid #e5e7eb'
											}}>
												<div style={{
													display: 'grid',
													gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
													gap: '8px'
												}}>
													{(editingListing.defect_images || [])?.map((image, index) => (
														<img
															key={index}
															src={image}
															alt={`Defect/wear ${index + 1}`}
															style={{
																width: '100%',
																height: '80px',
																objectFit: 'cover',
																borderRadius: '4px',
																border: '1px solid #e5e7eb',
																cursor: 'pointer'
															}}
															onClick={() => window.open(image, '_blank')}
															title="Click to view full size"
														/>
													))}
												</div>
												<div style={{
													fontSize: '12px',
													color: '#6b7280',
													marginTop: '8px',
													textAlign: 'center'
												}}>
													{editingListing.defect_images.length} image{editingListing.defect_images.length !== 1 ? 's' : ''} • Click to view full size
												</div>
											</div>
										) : (
											<div style={{
												padding: '16px',
												backgroundColor: '#f9fafb',
												borderRadius: '8px',
												border: '1px solid #e5e7eb',
												textAlign: 'center',
												color: '#6b7280'
											}}>
												No defects/wear images available
											</div>
										)}
									</div>
								</div>
							</div>

							{/* Status Field - Only show for editing existing listings */}
							{editingListing && (
								<div style={{
									marginBottom: '24px',
									padding: '20px',
									backgroundColor: '#f0f9ff',
									border: '2px solid #3b82f6',
									borderRadius: '12px',
									position: 'relative'
								}}>
									<div style={{
										position: 'absolute',
										top: '-12px',
										left: '16px',
										backgroundColor: '#3b82f6',
										color: 'white',
										padding: '4px 12px',
										borderRadius: '6px',
										fontSize: '12px',
										fontWeight: '600'
									}}>
										LISTING STATUS
									</div>

									<h3 style={{
										fontSize: '18px',
										fontWeight: '600',
										marginBottom: '16px',
										color: '#1e40af',
										marginTop: '8px'
									}}>
										Update Listing Status
									</h3>

									<div className="form-group" style={{ marginBottom: '0' }}>
										<label className="form-label" style={{ color: '#1e40af', fontWeight: '600' }}>
											Current Status
										</label>
										<select
											className="form-select"
											style={{
												border: '2px solid #3b82f6',
												backgroundColor: 'white'
											}}
											{...register('status', { required: 'Status is required' })}
										>
											<option value="pending">Pending - Awaiting Authentication</option>
											<option value="authenticated">Authenticated - Verified by Authenticator</option>
											<option value="listed">Listed - Available for Purchase</option>
											<option value="sold">Sold - Transaction Completed</option>
										</select>
										{errors.status && (
											<div className="error-message">{errors.status.message}</div>
										)}
										<div style={{
											fontSize: '12px',
											color: '#1e40af',
											marginTop: '8px',
											fontWeight: '500'
										}}>
											💡 Select the appropriate status based on the current workflow stage
										</div>
									</div>
								</div>
							)}

							<div style={{
								display: 'flex',
								gap: '12px',
								justifyContent: 'flex-end',
								marginTop: '24px'
							}}>
								<button
									type="button"
									onClick={closeModal}
									className="btn btn-secondary"
								>
									Cancel
								</button>
								<button type="submit" className="btn btn-primary">
									{editingListing ? 'Update Listing' : 'Create Listing'}
								</button>
							</div>
						</form>
					</div>
				</div>
			)}

			{/* View Modal */}
			{viewModal && viewingListing && (
				<div className="modal-overlay" onClick={closeModal}>
					<div
						className="modal"
						onClick={(e) => e.stopPropagation()}
						style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}
					>
						<div className="modal-header">
							<h2 className="modal-title">
								Listing #{viewingListing.listing_id}
							</h2>
							<button onClick={closeModal} className="close-btn">
								×
							</button>
						</div>

						<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
							{/* Images Section */}
							<div>
								<h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
									Images
								</h3>
								{viewingListing.dress_images && viewingListing.dress_images.length > 0 ? (
									<div style={{ position: 'relative' }}>
										<img
											src={viewingListing.dress_images[0]?.medium_path || viewingListing.dress_images[0]?.original_path}
											alt={`Listing ${viewingListing.listing_id}`}
											style={{
												width: '100%',
												height: '300px',
												objectFit: 'cover',
												borderRadius: '8px'
											}}
										/>
									</div>
								) : (
									<div style={{
										width: '100%',
										height: '300px',
										backgroundColor: '#f3f4f6',
										borderRadius: '8px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center'
									}}>
										<ImageIcon size={48} color="#9ca3af" />
									</div>
								)}
							</div>

							{/* Details Section */}
							<div>
								<h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
									Details
								</h3>

								<div style={{ marginBottom: '16px' }}>
									<div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
										<span style={{ fontWeight: '500', minWidth: '100px' }}>Status:</span>
										<div style={{
											display: 'flex',
											alignItems: 'center',
											gap: '4px',
											backgroundColor: getStatusColor(viewingListing.status),
											color: 'white',
											padding: '2px 8px',
											borderRadius: '12px',
											fontSize: '12px'
										}}>
											{React.createElement(getStatusIcon(viewingListing.status), { size: 12 })}
											{viewingListing.status_text || (typeof viewingListing.status === 'string' 
												? viewingListing.status.charAt(0).toUpperCase() + viewingListing.status.slice(1).replace('-', ' ') 
												: 'Unknown')}
										</div>
									</div>

									<div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
										<span style={{ fontWeight: '500', minWidth: '100px' }}>Designer:</span>
										<span>{viewingListing.designer_brand || 'N/A'}</span>
									</div>

									<div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
										<span style={{ fontWeight: '500', minWidth: '100px' }}>Seller:</span>
										<span>{viewingListing.seller?.full_name || 'N/A'}</span>
									</div>

									<div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
										<span style={{ fontWeight: '500', minWidth: '100px' }}>Price:</span>
										<span style={{ color: '#10b981', fontWeight: '600' }}>${viewingListing.pricing?.ask_price || 'N/A'}</span>
										<span style={{ color: '#6b7280', textDecoration: 'line-through' }}>
											${viewingListing.original_price}
										</span>
									</div>

									<div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
										<span style={{ fontWeight: '500', minWidth: '100px' }}>Size:</span>
										<span>{viewingListing.sizing_fit?.size || 'N/A'}</span>
									</div>

									<div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
										<span style={{ fontWeight: '500', minWidth: '100px' }}>Color:</span>
										<span>{viewingListing.colors || 'N/A'}</span>
									</div>

									<div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
										<span style={{ fontWeight: '500', minWidth: '100px' }}>Category:</span>
										<span>{viewingListing.category || 'N/A'}</span>
									</div>

									<div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
										<span style={{ fontWeight: '500', minWidth: '100px' }}>Fabric:</span>
										<span>{viewingListing.fabrics || 'N/A'}</span>
									</div>

									<div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
										<span style={{ fontWeight: '500', minWidth: '100px' }}>Work/Embroidery:</span>
										<span>{viewingListing.work_embroidery || 'N/A'}</span>
									</div>

									<div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
										<span style={{ fontWeight: '500', minWidth: '100px' }}>Purchase Date:</span>
										<span>{viewingListing.purchase_date || 'N/A'}</span>
									</div>

									<div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
										<span style={{ fontWeight: '500', minWidth: '100px' }}>Original Tags:</span>
										<span>{viewingListing.original_tags_present ? 'Yes' : 'No'}</span>
									</div>

									<div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
										<span style={{ fontWeight: '500', minWidth: '100px' }}>Condition:</span>
										<span>{viewingListing.condition?.times_worn || 'N/A'}</span>
									</div>
									{viewingListing.condition?.defects && viewingListing.condition.defects.length > 0 && (
										<div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
											<span style={{ fontWeight: '500', minWidth: '100px' }}>Defects:</span>
											<span>{Array.isArray(viewingListing.condition.defects) ? viewingListing.condition.defects.join(', ') : viewingListing.condition.defects}</span>
										</div>
									)}

									{viewingListing.authenticator_name && (
										<div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
											<span style={{ fontWeight: '500', minWidth: '100px' }}>Authenticated by:</span>
											<span>{viewingListing.authenticator_name}</span>
										</div>
									)}
								</div>

								<div style={{ marginBottom: '16px' }}>
									<h4 style={{ fontWeight: '500', marginBottom: '8px' }}>Description:</h4>
									<p style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>
										{viewingListing.description || 'No description provided'}
									</p>
								</div>

								{/* Additional Images */}
								{(viewingListing.proof_images?.length > 0 || viewingListing.defect_images?.length > 0) && (
									<div>
										<h4 style={{ fontWeight: '500', marginBottom: '12px' }}>Additional Images:</h4>

										{viewingListing.proof_images?.length > 0 && (
											<div style={{ marginBottom: '16px' }}>
												<span style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block' }}>
													Proof of Purchase:
												</span>
												<div style={{
													display: 'grid',
													gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
													gap: '8px'
												}}>
													{viewingListing.proof_images.map((image, index) => (
														<img
															key={index}
															src={image?.medium_path || image?.original_path}
															alt={`Proof ${index + 1}`}
															style={{
																width: '100%',
																height: '60px',
																objectFit: 'cover',
																borderRadius: '4px',
																border: '1px solid #e5e7eb',
																cursor: 'pointer'
															}}
															onClick={() => window.open(image?.original_path || image?.medium_path, '_blank')}
															title="Click to view full size"
														/>
													))}
												</div>
											</div>
										)}

										{viewingListing.defect_images?.length > 0 && (
											<div>
												<span style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block' }}>
													Defects/Wear:
												</span>
												<div style={{
													display: 'grid',
													gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
													gap: '8px'
												}}>
													{(viewingListing.defect_images || [])?.map((image, index) => (
														<img
															key={index}
															src={image?.medium_path || image?.original_path}
															alt={`Defect ${index + 1}`}
															style={{
																width: '100%',
																height: '60px',
																objectFit: 'cover',
																borderRadius: '4px',
																border: '1px solid #e5e7eb',
																cursor: 'pointer'
															}}
															onClick={() => window.open(image?.original_path || image?.medium_path, '_blank')}
															title="Click to view full size"
														/>
													))}
												</div>
											</div>
										)}
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			)}


			{/* Listing Wizard */}
			<ListingWizard
				isOpen={showWizard}
				onClose={handleWizardClose}
				onComplete={handleWizardComplete}
			/>

		</div>
	);
};

export default Listings;